import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { BsPencilSquare } from "react-icons/bs";
import { toast } from "react-toastify";
import moment from "moment";
import { useActionGate } from "../utils/menuNodes";
import {
  getFundRequestHistory,
  getStockRequestHistory,
  getOpenRequestRemarks,
  getPunchRequestRemarks,
  updateFundRemark,
  updateStockRemark,
  updateTransferDetails,
} from "../services/contractorApi";

// Item remarks shown and edited INSIDE the item tables (Approve / Transfer),
// not in a separate panel (2026-11-02). Each item keeps a Request remark (the
// Create copy) and an Approve remark (the Approve copy); both are read from the
// request's item JSON and saved back to it by the item's path.
//
//   const remarks = useItemRemarkMap("fund" | "stock", requestId);
//   <ItemRemarksCell remarks={remarks} path="0:request_fund:3" stages={["raised", "approved"]} canEdit />

export const useItemRemarkMap = (type, requestId) => {
  const [map, setMap] = useState({ raised: {}, approved: {}, transfers: [] });
  const load = useCallback(async () => {
    if (!requestId) return;
    const res = await (type === "stock" ? getStockRequestHistory : getFundRequestHistory)(requestId);
    if (res?.status && res.item_remark_map) setMap(res.item_remark_map);
  }, [type, requestId]);
  useEffect(() => {
    load();
  }, [load]);

  const save = async (stage, path, field, remark) => {
    const res = await (type === "stock" ? updateStockRemark : updateFundRemark)({
      source: "item",
      request_id: requestId,
      stage,
      field,
      path,
      remark,
    });
    if (res?.status) {
      toast.success(res.message || "Remark updated");
      await load();
      return true;
    }
    toast.error(res?.message || "Could not update the remark");
    return false;
  };
  // A transfer's Transaction ID + Remark — the same save the Transferred
  // list's Edit used (it updates every copy: bank ledger, employee / supplier
  // ledger, request history). Fund saves by the transfer's own id, stock by
  // the request id.
  const saveTransfer = async (tr, transactionId, remark) => {
    const res = await updateTransferDetails(type === "stock" ? "stock" : "fund", {
      id: type === "stock" ? requestId : tr.id,
      transaction_id: transactionId,
      remark,
    });
    if (res?.status) {
      toast.success(res.message || "Transfer details updated");
      await load();
      return true;
    }
    toast.error(res?.message || "Could not update the transfer");
    return false;
  };
  return { map, save, saveTransfer, reload: load, type, requestId };
};

const LABEL = { raised: "Request", approved: "Approve" };

const RemarkLine = ({ remarks, stage, path, canEdit }) => {
  const entry = remarks?.map?.[stage]?.[path];
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  if (!entry) return null;

  const onSave = async () => {
    setSaving(true);
    const ok = await remarks.save(stage, path, entry.field, editing);
    setSaving(false);
    if (ok) setEditing(null);
  };

  return (
    <div className="mb-1" style={{ textTransform: "uppercase", minWidth: 180 }}>
      <span className="fw-bold">{LABEL[stage]}: </span>
      {editing !== null ? (
        <>
          <Form.Control
            as="textarea"
            style={{ textTransform: "uppercase" }}
            rows={2}
            maxLength={1000}
            value={editing}
            onChange={(e) => setEditing(e.target.value)}
          />
          <div className="d-flex gap-1 mt-1">
            <Button size="sm" variant="success" onClick={onSave} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Save"}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(null)} disabled={saving}>
              ✕
            </Button>
          </div>
        </>
      ) : (
        <>
          {entry.remark || <span className="text-muted">-</span>}
          {canEdit && (
            <BsPencilSquare
              role="button"
              title={`Edit ${LABEL[stage]} remark`}
              className="ms-2 text-danger"
              onClick={() => setEditing(entry.remark || "")}
            />
          )}
        </>
      )}
    </div>
  );
};

// The transfer remark is the whole request's (one per payment): every item row
// shows it, read-only — it is edited from the Transferred list, which updates
// every copy of it.
// One transfer covers several items, so its Transaction ID is NOT edited from
// an item row (it is edited once, per transfer, in the View page's Transfer
// Details table) — only the remark is, and the Transaction ID is saved as is.
const TransferLine = ({ remarks, tr, canEdit }) => {
  const [editing, setEditing] = useState(null); // { remark }
  const [saving, setSaving] = useState(false);
  const onSave = async () => {
    setSaving(true);
    const ok = await remarks.saveTransfer(tr, String(tr.transaction_id || "").trim(), String(editing.remark || "").trim());
    setSaving(false);
    if (ok) setEditing(null);
  };
  return (
    <div className="mb-1" style={{ textTransform: "uppercase", minWidth: 180 }}>
      <span className="fw-bold">
        Transfer{tr.at ? ` (${moment(tr.at).format("DD-MM-YYYY")})` : ""}:{" "}
      </span>
      {editing ? (
        <>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="Remark"
            maxLength={1000}
            style={{ textTransform: "uppercase" }}
            value={editing.remark}
            onChange={(e) => setEditing({ ...editing, remark: e.target.value })}
          />
          <div className="d-flex gap-1 mt-1">
            <Button size="sm" variant="success" onClick={onSave} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Save"}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(null)} disabled={saving}>
              ✕
            </Button>
          </div>
        </>
      ) : (
        <>
          {tr.remark || "-"}
          {/* pencil right after the remark, on the same line as the others */}
          {canEdit && (
            <BsPencilSquare
              role="button"
              title="Edit transfer remark"
              className="ms-2 text-danger"
              onClick={() => setEditing({ remark: tr.remark || "" })}
            />
          )}
          {tr.transaction_id ? <div className="text-gray">Txn ID: {tr.transaction_id}</div> : null}
        </>
      )}
    </div>
  );
};

const TransferLines = ({ remarks, canEdit }) => {
  const list = remarks?.map?.transfers || [];
  if (!list.length) return null;
  return list.map((tr) => <TransferLine key={tr.id} remarks={remarks} tr={tr} canEdit={canEdit} />);
};

export const ItemRemarksCell = ({ remarks, path, stages = ["raised", "approved"], canEdit = false, canEditTransfer = false }) => (
  <>
    {stages.map((s) =>
      s === "transferred" ? (
        <TransferLines key={s} remarks={remarks} canEdit={canEditTransfer} />
      ) : (
        <RemarkLine key={s} remarks={remarks} stage={s} path={path} canEdit={canEdit} />
      ),
    )}
  </>
);

// ── Punch screens ────────────────────────────────────────────────────────────
// A punch row is an ITEM whose money can come from several requests, so its
// earlier remarks are every one of those requests' Request / Approve remarks
// for that item, plus each request's Transfer remark — listed per request
// inside the row's own Remark cell.
//   source: { userId } — the employee's open requests (create / pending)
//           { punch: { punchId, pending } | { userId, complaintId, date } } — the
//             requests one punch row drew from (view / approve)
export const usePunchRequestRemarks = (type, source) => {
  const [data, setData] = useState({ item_remarks: [], steps: [] });
  const key = JSON.stringify(source || {});
  const load = useCallback(async () => {
    const src = JSON.parse(key);
    let res = null;
    if (src.punch && (src.punch.punchId || src.punch.userId)) res = await getPunchRequestRemarks(type, src.punch);
    else if (src.userId) res = await getOpenRequestRemarks(type, src.userId);
    if (!res) return;
    setData({
      item_remarks: Array.isArray(res?.item_remarks) ? res.item_remarks : [],
      steps: Array.isArray(res?.data) ? res.data : [],
    });
  }, [type, key]);
  useEffect(() => {
    load();
  }, [load]);

  const save = async (r, remark) => {
    const res = await (type === "stock" ? updateStockRemark : updateFundRemark)({
      source: "item",
      request_id: r.request_id,
      stage: r.action,
      field: r.field,
      path: r.path,
      remark,
    });
    if (res?.status) {
      toast.success(res.message || "Remark updated");
      await load();
      return true;
    }
    toast.error(res?.message || "Could not update the remark");
    return false;
  };
  return { ...data, save };
};

const norm = (v) => String(v || "").trim().toLowerCase();

const PunchRemarkLine = ({ r, save, canEdit }) => {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const onSave = async () => {
    setSaving(true);
    const ok = await save(r, editing);
    setSaving(false);
    if (ok) setEditing(null);
  };
  return (
    <div style={{ textTransform: "uppercase" }}>
      <span className="fw-bold">{LABEL[r.action] || r.action}: </span>
      {editing !== null ? (
        <>
          <Form.Control as="textarea"
            style={{ textTransform: "uppercase" }} rows={2} maxLength={1000} value={editing} onChange={(e) => setEditing(e.target.value)} />
          <div className="d-flex gap-1 mt-1">
            <Button size="sm" variant="success" onClick={onSave} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Save"}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(null)} disabled={saving}>
              ✕
            </Button>
          </div>
        </>
      ) : (
        <>
          {r.remark}
          {canEdit && (
            <BsPencilSquare
              role="button"
              title={`Edit ${LABEL[r.action] || ""} remark`}
              className="ms-2 text-danger"
              onClick={() => setEditing(r.remark || "")}
            />
          )}
        </>
      )}
    </div>
  );
};

export const PunchItemRemarksCell = ({ remarks, itemName, canEdit = false }) => {
  const name = norm(itemName);
  if (!name) return null;
  const mine = (remarks?.item_remarks || []).filter((r) => {
    const n = norm(r.item);
    return n === name || name.startsWith(n) || n.startsWith(name);
  });
  const reqIds = [...new Set(mine.map((r) => r.request_id))];
  if (!reqIds.length) return null;
  return (
    <div className="mb-1" style={{ minWidth: 200 }}>
      {reqIds.map((rid) => {
        const rows = mine.filter((r) => r.request_id === rid);
        const transfers = (remarks?.steps || []).filter(
          (s) => s.request_id === rid && s.action === "transferred" && String(s.remark || "").trim(),
        );
        return (
          <div key={rid} className="border-bottom pb-1 mb-1">
            <div className="text-gray" style={{ textTransform: "uppercase" }}>{rows[0]?.request_unique_id || `#${rid}`}</div>
            {rows.map((r) => (
              <PunchRemarkLine key={`${r.action}|${r.path}`} r={r} save={remarks.save} canEdit={canEdit} />
            ))}
            {transfers.map((s, i) => (
              <div key={i} style={{ textTransform: "uppercase" }}>
                <span className="fw-bold">Transfer: </span>
                <span style={{ textTransform: "uppercase" }}>{s.remark}</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

// ── One request's remarks for one item row ───────────────────────────────────
// A punch row that knows the request it draws from (a stock batch carries its
// stock_request_id): that request's Request / Approve remarks for the row's
// item — shown even when blank, so one can be written — and the request's
// Transfer remark with its Transaction ID.
export const RequestItemRemarksCell = ({ type, requestId, itemName, canEdit = false }) => {
  const remarks = useItemRemarkMap(type, requestId);
  // the Transfer line's Transaction ID / Remark: the "Edit Transaction ID /
  // Remark" right of the Transferred tab, the same right as on the View page
  const canActOn = useActionGate(type === "stock" ? "/StockManagement" : "/FundManagement");
  const canEditTransfer = canActOn(type === "stock" ? "stock-transfer-transferred" : "fund-transfer-transferred", "edit_details", false);
  const name = norm(itemName);
  const pick = (part) =>
    Object.keys(remarks.map?.[part] || {}).find((k) => {
      const n = norm(remarks.map[part][k]?.item);
      return n === name || (name && (name.startsWith(n) || n.startsWith(name)));
    });
  const path = pick("approved") || pick("raised");
  if (!requestId) return null;
  return (
    <div className="mb-1" style={{ minWidth: 200 }}>
      {path ? (
        <ItemRemarksCell remarks={remarks} path={path} stages={["raised", "approved", "transferred"]} canEdit={canEdit} canEditTransfer={canEditTransfer} />
      ) : (
        <ItemRemarksCell remarks={remarks} path="" stages={["transferred"]} canEditTransfer={canEditTransfer} />
      )}
    </div>
  );
};

// ── Earlier punches' remarks ─────────────────────────────────────────────────
// "Punch (date): remark ✎" for punches already made from this row's batch;
// saved through the request's update-remark (source "punch"). `list` items:
// { id, remark, punch_at, qty }.
const PunchLine = ({ type, p, canEdit }) => {
  const [text, setText] = useState(p.remark || "");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const onSave = async () => {
    setSaving(true);
    const res = await (type === "stock" ? updateStockRemark : updateFundRemark)({ source: "punch", id: p.id, remark: editing });
    setSaving(false);
    if (res?.status) {
      toast.success(res.message || "Remark updated");
      setText(editing);
      setEditing(null);
    } else toast.error(res?.message || "Could not update the remark");
  };
  return (
    <div className="mb-1" style={{ textTransform: "uppercase" }}>
      <span className="fw-bold">Punch{p.punch_at ? ` (${moment(p.punch_at).format("DD-MM-YYYY")})` : ""}: </span>
      {editing !== null ? (
        <>
          <Form.Control as="textarea" rows={2} maxLength={255} style={{ textTransform: "uppercase" }} value={editing} onChange={(e) => setEditing(e.target.value)} />
          <div className="d-flex gap-1 mt-1">
            <Button size="sm" variant="success" onClick={onSave} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : "Save"}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(null)} disabled={saving}>
              ✕
            </Button>
          </div>
        </>
      ) : (
        <>
          {text || "-"}
          {canEdit && (
            <BsPencilSquare role="button" title="Edit punch remark" className="ms-2 text-danger" onClick={() => setEditing(text)} />
          )}
        </>
      )}
    </div>
  );
};

export const PunchHistoryLines = ({ type = "stock", list = [], canEdit = false }) =>
  (Array.isArray(list) ? list : []).map((p) => <PunchLine key={p.id} type={type} p={p} canEdit={canEdit} />);
